import {
  BasicButton,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Modal,
  PrimaryButton,
  Text,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { useTranslation } from "react-i18next";

export type RequestorConfigLossConfirmModalProps = {
  isOpen: boolean;
  userName: string;
  targetRoleName: string;
  primaryReviewerName: string | null;
  additionalNotificationEmailCount: number;
  isLoading?: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
};

export function RequestorConfigLossConfirmModal({
  isOpen,
  userName,
  targetRoleName,
  primaryReviewerName,
  additionalNotificationEmailCount,
  isLoading = false,
  onClose,
  onConfirm,
}: RequestorConfigLossConfirmModalProps) {
  const { t } = useTranslation();

  return (
    <Modal isOpen={isOpen}>
      <Dialog>
        <DialogHeader onClose={isLoading ? undefined : onClose}>
          {t("Remove Requestor role from {{userName}}?", { userName })}
        </DialogHeader>
        <DialogContent>
          <Text style="global/body-primary">
            {t(
              "Changing {{userName}} to {{targetRoleName}} will permanently remove their Requestor configuration, including:",
              { userName, targetRoleName }
            )}
          </Text>
          <ul className={styles.list}>
            {primaryReviewerName ? (
              <li>
                <Text style="global/body-primary">
                  {t("Primary reviewer: {{name}}", {
                    name: primaryReviewerName,
                  })}
                </Text>
              </li>
            ) : null}
            {additionalNotificationEmailCount > 0 ? (
              <li>
                <Text style="global/body-primary">
                  {t("{{count}} additional notification email(s)", {
                    count: additionalNotificationEmailCount,
                  })}
                </Text>
              </li>
            ) : null}
            {!primaryReviewerName && additionalNotificationEmailCount === 0 ? (
              <li>
                <Text style="global/body-primary">
                  {t(
                    "Reviewer assignment and additional notification settings"
                  )}
                </Text>
              </li>
            ) : null}
          </ul>
        </DialogContent>
        <DialogFooter>
          <BasicButton
            onPress={onClose}
            isDisabled={isLoading}
            testID="requestor-config-loss-cancel"
          >
            {t("Cancel")}
          </BasicButton>
          <PrimaryButton
            onPress={() => void onConfirm()}
            isLoading={isLoading}
            isDisabled={isLoading}
            testID="requestor-config-loss-confirm"
          >
            {t("Continue with role change")}
          </PrimaryButton>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
}

const styles = {
  list: css({
    marginTop: "var(--space-2)",
    paddingLeft: "var(--space-4)",
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-1)",
  }),
};
